import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { getUsersSummary } from "../../../../api/api";
import { selectStyles } from "../../../../utils/selectStyles";
import "../../../ProjectsV2/ProjectFinance/Estimate/ApprovalModal/ApprovalModal.css";

const getUserFullName = (user) =>
  [user?.surname, user?.name, user?.patronymic].filter(Boolean).join(" ").trim();

const getUserTitles = (user) => {
  const access = (user?.access_levels || []).map((lvl) =>
    typeof lvl === "string" ? lvl : lvl?.name,
  );
  const roles = (user?.roles || []).map((role) =>
    typeof role === "string" ? role : role?.name,
  );
  return [...new Set([...access, ...roles])].filter(Boolean);
};

const findUserByRoleIncludes = (users, rolePart) => {
  const needle = String(rolePart || "").toLowerCase();
  return users.find((u) =>
    getUserTitles(u).some((title) => String(title).toLowerCase().includes(needle)),
  );
};

const SalaryApprovalModal = ({ open, onClose, onSubmit, departmentName, period }) => {
  const [approvers, setApprovers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultApprovers = useMemo(() => {
    const directorUser = usersList.find((u) => Number(u.id) === 41);
    const financialUser = usersList.find((u) => Number(u.id) === 62);
    const branchHeadUser =
      findUserByRoleIncludes(usersList, "руководитель отделения") ||
      findUserByRoleIncludes(usersList, "начальник отделения") ||
      findUserByRoleIncludes(usersList, "руководитель отдела");

    const fallbackDirector = {
      id: 41,
      title: "Директор",
      name: "Бекбауов Серикпай",
    };
    const fallbackFinancial = {
      id: 62,
      title: "Финансовый руководитель",
      name: "Тлеубаева Бакыт",
    };

    const mapped = [
      directorUser
        ? {
            id: directorUser.id,
            title: "Директор",
            name: getUserFullName(directorUser) || fallbackDirector.name,
          }
        : fallbackDirector,
      financialUser
        ? {
            id: financialUser.id,
            title: "Финансовый руководитель",
            name: getUserFullName(financialUser) || fallbackFinancial.name,
          }
        : fallbackFinancial,
      branchHeadUser
        ? {
            id: branchHeadUser.id,
            title: "Руководитель отделения",
            name: getUserFullName(branchHeadUser) || "Не назначен",
          }
        : null,
    ]
      .filter(Boolean)
      .filter(
        (item, index, arr) =>
          arr.findIndex((it) => Number(it.id) === Number(item.id)) === index,
      );

    return mapped;
  }, [usersList]);

  useEffect(() => {
    if (!open) return;
    setSelectedUserToAdd(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setUsersLoading(true);
      try {
        const response = await getUsersSummary({ isActive: true, pageSize: 1000 });
        setUsersList(response?.users || []);
      } catch (error) {
        console.error("Ошибка загрузки пользователей для согласования зарплаты:", error);
        setUsersList([]);
      } finally {
        setUsersLoading(false);
      }
    };
    load();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (defaultApprovers.length > 0) {
      setApprovers(defaultApprovers);
    }
  }, [open, defaultApprovers]);

  if (!open) return null;

  const handleRemoveApprover = (id) => {
    setApprovers((prev) => prev.filter((approver) => Number(approver.id) !== Number(id)));
  };

  const addApproverFromUser = (user) => {
    if (!user) return;
    if (approvers.some((approver) => Number(approver.id) === Number(user.id))) return;
    setApprovers((prev) => [
      ...prev,
      {
        id: user.id,
        title: getUserTitles(user)[0] || "Согласующий",
        name: getUserFullName(user) || "Без имени",
      },
    ]);
    setSelectedUserToAdd(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (approvers.length === 0) return;

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(approvers.map((a) => a.id));
      }
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="approval-modal-backdrop" onClick={onClose} />
      <div className="approval-modal">
        <div className="approval-modal-title">Согласование зарплаты</div>
        
        {/* Информация об отделе и периоде */}
        {(departmentName || period) && (
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            marginBottom: "18px",
            fontSize: "16px",
            color: "#666"
          }}>
            {departmentName && (
              <span>{departmentName}</span>
            )}
            {period && (
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>📅</span>
                {period}
              </span>
            )}
          </div>
        )}

        <div className="approval-modal-warning">
          <span className="approval-modal-warning-icon">⚠️</span>
          <span>
            Вы отправляете на согласование расчёт зарплат за выбранный период для
            всего отдела.
          </span>
        </div>
        <div className="approval-modal-label">
          Выберите кому отправить на согласование:
        </div>
        <form onSubmit={handleSubmit}>
          <div className="approval-modal-list">
            {approvers.map((a) => (
              <div key={a.id} className="approval-modal-list-row">
                <div className="approval-modal-approver-info">
                  <span className="approval-checkbox-title">{a.title}</span>
                  <span className="approval-checkbox-name">{a.name}</span>
                </div>
                <button
                  type="button"
                  className="approval-modal-remove-btn"
                  onClick={() => handleRemoveApprover(a.id)}
                  disabled={isSubmitting}
                  title="Удалить из списка"
                  aria-label="Удалить из списка"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="approval-modal-add-user">
            <div className="approval-modal-add-user-label">
              Добавить согласующего из списка пользователей:
            </div>
            <Select
              isClearable
              placeholder={usersLoading ? "Загрузка..." : "Выберите пользователя"}
              isDisabled={usersLoading || isSubmitting}
              options={usersList
                .filter(
                  (u) =>
                    !approvers.some(
                      (a) => Number(a.id) === Number(u.id),
                    ),
                )
                .map((u) => ({
                  value: u.id,
                  label: getUserFullName(u) || "Без имени",
                  userData: u,
                }))}
              value={selectedUserToAdd}
              onChange={(option) => {
                setSelectedUserToAdd(option);
                if (option?.userData) {
                  addApproverFromUser(option.userData);
                }
              }}
              styles={selectStyles}
              className="approval-modal-user-select"
            />
          </div>
          <div className="approval-modal-actions">
            <button
              type="button"
              className="approval-btn outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="approval-btn green"
              disabled={isSubmitting || approvers.length === 0}
            >
              {isSubmitting ? "Отправка..." : "Отправить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SalaryApprovalModal;

